import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import AttorneySelect from '../components/AttorneySelect';
import MergeDocument from './MergeDocument';
import OrderedBySelect from '../components/OrderedBySelect';
import InsuranceCompanySelect from '../components/InsuranceCompanySelect';
import AdjusterSelect from '../components/AdjusterSelect';
import StopLocationSelect from '../components/StopLocationSelect';
import QuestionSelect from '../components/QuestionSelect';
import TypeOfRecordSelect from '../components/TypeOfRecordSelect';

const EMPTY_ATTY = { name: '', firm: '', bar_num: '', addr1: '', addr2: '', city: '', state_code: '', zip: '', phone: '', fax: '', email: '' };

const API_URL = process.env.REACT_APP_API_URL || '';

const CASE_TYPES = ['DISTRICT', 'COUNTY', 'JUSTICE OF THE PEACE', 'FEDERAL'];
const OBTAIN_BY = ['Subpoena Duces Tecum - Admissible', 'Authorization', 'HIPAA'];
const BILL_TO_OPTIONS = ['Law Firm w/Insurance Rate', 'Law Firm', 'Insurance Company', 'Client'];
const STATUSES = [
  'Order Email Received',
  'Order Intake',
  'Order Processing',
  'Legal Tech',
  'Production Front',
  'Invoicing',
  'Production Back',
  'Completed',
  'On Hold',
  'Cancelled',
];
const RECORD_TYPES = ['Medical', 'Billing', 'Imaging', 'All Records'];

const STATUS_COLORS = {
  'Pending': 'bg-blue-100 text-blue-800',
  'In Progress': 'bg-yellow-100 text-yellow-800',
  'Completed': 'bg-green-100 text-green-800',
  'On Hold': 'bg-orange-100 text-orange-800',
  'Cancelled': 'bg-red-100 text-red-800',
};

function maskSSN(ssn) {
  if (!ssn || ssn.length < 4) return ssn || '';
  return '***-**-' + ssn.slice(-4);
}

const Field = ({ label, field, type = 'text', readOnly, form, set, editing, ...props }) => {
  const isRO = readOnly || !editing;
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
      <input
        type={type}
        value={form[field] || ''}
        onChange={(e) => set(field, e.target.value)}
        readOnly={isRO}
        className={`w-full px-2 py-1.5 text-sm border rounded ${isRO ? 'bg-gray-50 border-gray-200' : 'border-gray-300 focus:ring-1 focus:ring-blue-500 focus:outline-none'}`}
        {...props}
      />
    </div>
  );
};

const SelectField = ({ label, field, options, readOnly, form, set, editing }) => {
  const isRO = readOnly || !editing;
  if (isRO) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
        <div className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded">{form[field] || '-'}</div>
      </div>
    );
  }
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
      <select
        value={form[field] || ''}
        onChange={(e) => set(field, e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <option value="">-- Select --</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
};

export default function CauseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [cause, setCause] = useState(null);
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.tab ?? 0);
  const [editing, setEditing] = useState(location.state?.editing ?? false);
  const [form, setForm] = useState({});
  const [selectedWO, setSelectedWO] = useState(null);
  const [woForm, setWoForm] = useState({});
  const [clientAtty, setClientAtty] = useState({ ...EMPTY_ATTY });
  const [opposingAtty, setOpposingAtty] = useState({ ...EMPTY_ATTY });
  const [clientAttyType, setClientAttyType] = useState('Client');
  const [opposingAttyType, setOpposingAttyType] = useState('Opposing');
  const [clientExpanded, setClientExpanded] = useState(true);
  const [opposingExpanded, setOpposingExpanded] = useState(true);
  const [additionalAttys, setAdditionalAttys] = useState([]);
  const [showAddAttyForm, setShowAddAttyForm] = useState(false);
  const [addAttyForm, setAddAttyForm] = useState({ ...EMPTY_ATTY });
  const [invForm, setInvForm] = useState({ amount: '', description: '', status: 'Draft', invoice_date: '', due_date: '', notes: '' });
  const [showInvForm, setShowInvForm] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [allCauseInvoices, setAllCauseInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [showInvoiceMerge, setShowInvoiceMerge] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyForm, setCopyForm] = useState({});
  const [copying, setCopying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedCompanyOriginalId, setSelectedCompanyOriginalId] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/causes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCause(res.data);
      setForm(res.data);
      if (res.data.work_orders?.length > 0 && !selectedWO) {
        const wo = res.data.work_orders[0];
        setSelectedWO(wo);
        setWoForm(wo);
        loadInvoices(wo.id);
      }
    } catch (err) {
      console.error('Failed to load cause', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [id, token]);

  const loadInvoices = async (woId) => {
    try {
      const res = await axios.get(`${API_URL}/api/work-orders/${woId}/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(res.data);
    } catch (err) {
      console.error('Failed to load invoices', err);
    }
  };

  const loadAllCauseInvoices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/causes/${id}/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllCauseInvoices(res.data);
    } catch (err) {
      console.error('Failed to load cause invoices', err);
    }
  };

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!cause?.attorneys) return;
    const apiToForm = (a) => ({
      name: a.attorney_name || '', firm: a.firm_name || '', bar_num: a.bar_number || '',
      addr1: a.address || '', addr2: a.addr2 || '', city: a.city || '',
      state_code: a.state_code || '', zip: a.zip_code || '', phone: a.phone || '',
      fax: a.fax || '', email: a.email || '',
    });
    const client = cause.attorneys.find(a => ['Client','Plaintiff'].includes(a.type));
    const opposing = cause.attorneys.find(a => ['Opposing','Defendant'].includes(a.type));
    const additional = cause.attorneys.filter(a => !['Client','Plaintiff','Opposing','Defendant'].includes(a.type));
    setClientAtty(client ? apiToForm(client) : { ...EMPTY_ATTY });
    setClientAttyType(client?.type || 'Plaintiff');
    setOpposingAtty(opposing ? apiToForm(opposing) : { ...EMPTY_ATTY });
    setOpposingAttyType(opposing?.type || 'Defendant');
    setAdditionalAttys(additional.map(apiToForm));
  }, [cause]);

  const saveCause = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/causes/${id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditing(false);
      load();
    } catch (err) {
      console.error('Failed to save', err);
    } finally {
      setSaving(false);
    }
  };

  const saveWO = async () => {
    if (!selectedWO) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/work-orders/${selectedWO.id}`, woForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      load();
    } catch (err) {
      console.error('Failed to save WO', err);
    } finally {
      setSaving(false);
    }
  };

  const saveAttorneys = async () => {
    setSaving(true);
    try {
      for (const a of (cause.attorneys || [])) {
        await axios.delete(`${API_URL}/api/attorneys/${a.id}`, { headers: { Authorization: `Bearer ${token}` } });
      }
      const headers = { Authorization: `Bearer ${token}` };
      const toApi = (f, type) => ({
        type, attorney_name: f.name || '', firm_name: f.firm, bar_number: f.bar_num,
        address: f.addr1, addr2: f.addr2, city: f.city, state_code: f.state_code,
        zip_code: f.zip, phone: f.phone, fax: f.fax, email: f.email,
      });
      if (clientAtty.name || clientAtty.firm) {
        await axios.post(`${API_URL}/api/causes/${id}/attorneys`, toApi(clientAtty, clientAttyType), { headers });
      }
      if (opposingAtty.name || opposingAtty.firm) {
        await axios.post(`${API_URL}/api/causes/${id}/attorneys`, toApi(opposingAtty, opposingAttyType), { headers });
      }
      for (const a of additionalAttys) {
        if (a.name || a.firm) {
          await axios.post(`${API_URL}/api/causes/${id}/attorneys`, toApi(a, 'Additional'), { headers });
        }
      }
      load();
    } catch (err) {
      console.error('Failed to save attorneys', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAttySelect = (setAtty) => (_id, name, _firm, _phone, _email, fullObj) => {
    if (fullObj) {
      setAtty({
        name, firm: fullObj.firm_name || '', bar_num: fullObj.state_bar_number || '',
        addr1: fullObj.address || '', addr2: fullObj.address2 || '', city: fullObj.city || '',
        state_code: fullObj.state_code || '', zip: fullObj.zip_code || '',
        phone: fullObj.phone || '', fax: fullObj.fax || '', email: fullObj.email || '',
      });
    } else {
      setAtty(prev => ({ ...prev, name }));
    }
  };

  const setClientField = (field, value) => setClientAtty(prev => ({ ...prev, [field]: value }));
  const setOpposingField = (field, value) => setOpposingAtty(prev => ({ ...prev, [field]: value }));

  const saveInvoice = async () => {
    if (!selectedWO) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/work-orders/${selectedWO.id}/invoices`, {
        ...invForm,
        amount: parseFloat(invForm.amount) || 0,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowInvForm(false);
      setInvForm({ amount: '', description: '', status: 'Draft', invoice_date: '', due_date: '', notes: '' });
      loadInvoices(selectedWO.id);
    } catch (err) {
      console.error('Failed to save invoice', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-gray-500">Loading...</div>;
  if (!cause) return <div className="p-8 text-red-500">Cause not found</div>;

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const setWo = (field, value) => setWoForm((f) => ({ ...f, [field]: value }));

  const tabs = ['Case Details', 'Attorneys', 'Stop Location', 'Work Order Update', 'Work Order Pg 2', 'Invoice'];

  const selectWorkOrder = (wo) => {
    setSelectedWO(wo);
    setWoForm(wo);
    loadInvoices(wo.id);
  };

  return (
    <div>
      {/* Title Bar */}
      <div className="flex flex-wrap items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-800">
            Patient: {cause.patient_name} &nbsp; BD: {cause.patient_dob || '-'} &nbsp; SSN: {maskSSN(cause.patient_ssn)}
          </h1>
          {cause.rush_order && (
            <span className="inline-block mt-1 px-3 py-1 text-xs font-bold bg-red-100 text-red-800 rounded-full">RUSH ORDER</span>
          )}
        </div>
        <div className="flex gap-2 mt-2 md:mt-0">
          <button onClick={() => navigate('/search')} className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Search</button>
          <button onClick={() => setShowMerge(true)} className="px-3 py-1.5 text-sm text-white rounded" style={{ backgroundColor: '#1a2744' }}>📄 Merge</button>
          <button
            onClick={() => {
              setCopyForm({
                cause_number: (cause.cause_number || '') + '-COPY',
                patient_name: cause.patient_name || '',
                patient_last_name: cause.patient_last_name || '',
                patient_dob: cause.patient_dob || '',
                style_plaintiff: cause.style_plaintiff || '',
                style_defendant: cause.style_defendant || '',
                county: cause.county || '',
                court_number: cause.court_number || '',
                type_of_case: cause.type_of_case || '',
                file_number: '',
                order_date: new Date().toISOString().split('T')[0],
              });
              setShowCopyModal(true);
            }}
            className="px-3 py-1.5 text-sm text-white rounded"
            style={{ backgroundColor: '#2d6a4f' }}
          >
            📋 Copy Case
          </button>
          <button onClick={() => navigate(`/causes/${id}/work-orders/new`)} className="px-3 py-1.5 text-sm text-white rounded" style={{ backgroundColor: '#1a2744' }}>+ Work Order</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap border-b mb-4 gap-0">
        {tabs.map((t, i) => (
          <button
            key={t}
            onClick={() => setTab(i)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === i ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Tab 0: Case Details */}
      {tab === 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-end mb-3">
            {editing ? (
              <div className="flex gap-2">
                <button onClick={saveCause} disabled={saving} className="px-3 py-1.5 text-sm text-white rounded" style={{ backgroundColor: '#1a2744' }}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button onClick={() => { setEditing(false); setForm(cause); }} className="px-3 py-1.5 text-sm bg-gray-200 rounded">Cancel</button>
              </div>
            ) : (
              <button onClick={() => setEditing(true)} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">Edit</button>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-700 border-b pb-1">Court Information</h3>
              <Field label="Cause Number" field="cause_number" form={form} set={set} editing={editing} />
              <SelectField label="Type of Case" field="type_of_case" options={CASE_TYPES} form={form} set={set} editing={editing} />
              <Field label="County" field="county" form={form} set={(f,v)=>set(f,v.toUpperCase())} editing={editing} style={{textTransform:'uppercase'}} />
              <Field label="Court Number" field="court_number" form={form} set={set} editing={editing} />
              <SelectField label="Obtain Records By" field="obtain_records_by" options={OBTAIN_BY} form={form} set={set} editing={editing} />
              <h3 className="font-semibold text-gray-700 border-b pb-1 pt-2">Style</h3>
              <Field label="Plaintiff" field="style_plaintiff" form={form} set={(f,v)=>set(f,v.toUpperCase())} editing={editing} style={{textTransform:'uppercase'}} />
              <Field label="Defendant" field="style_defendant" form={form} set={(f,v)=>set(f,v.toUpperCase())} editing={editing} style={{textTransform:'uppercase'}} />
            </div>
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-700 border-b pb-1">Order</h3>
              <div className="grid grid-cols-2 gap-3">
                {editing ? (
                  <OrderedBySelect
                    label="Ordered By"
                    value={form.ordered_by}
                    onChange={(_id, name, email) => {
                      set('ordered_by', name);
                      if (email) set('ordered_by_email', email);
                    }}
                  />
                ) : (
                  <Field label="Ordered By" field="ordered_by" form={form} set={set} editing={editing} />
                )}
                <Field label="Email" field="ordered_by_email" form={form} set={set} editing={editing} />
                <Field label="Order Date" field="order_date" type="date" form={form} set={set} editing={editing} />
                <Field label="Date Needed" field="date_needed" type="date" form={form} set={set} editing={editing} />
              </div>
              <Field label="File Number" field="file_number" form={form} set={set} editing={editing} />
              <h3 className="font-semibold text-gray-700 border-b pb-1 pt-2">Patient</h3>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Full Name" field="patient_name" form={form} set={set} editing={editing} />
                <Field label="Last Name" field="patient_last_name" form={form} set={set} editing={editing} />
                <Field label="DOB" field="patient_dob" type="date" form={form} set={set} editing={editing} />
                <Field label="SSN" field="patient_ssn" form={form} set={set} editing={editing} />
              </div>
              <SelectField label="Bill To" field="bill_to" options={BILL_TO_OPTIONS} form={form} set={set} editing={editing} />
              <h3 className="font-semibold text-gray-700 border-b pb-1 pt-2">Insurance</h3>
              <div className="grid grid-cols-2 gap-3">
                {editing ? (
                  <InsuranceCompanySelect
                    value={form.insurance_company || ''}
                    label="Company"
                    onChange={(id, companyName, address1, cityStZip, originalId) => {
                      set('insurance_company', companyName);
                      const addr = [address1, cityStZip].filter(Boolean).join(' ');
                      set('insurance_address', addr);
                      setSelectedCompanyOriginalId(originalId || '');
                    }}
                  />
                ) : (
                  <Field label="Company" field="insurance_company" form={form} set={set} editing={editing} />
                )}
                {editing ? (
                  <AdjusterSelect
                    value={form.adjuster_name || ''}
                    label="Adjuster"
                    companyOriginalId={selectedCompanyOriginalId}
                    onChange={(id, name, email, phone) => {
                      set('adjuster_name', name);
                      set('adjuster_email', email);
                    }}
                  />
                ) : (
                  <Field label="Adjuster" field="adjuster_name" form={form} set={set} editing={editing} />
                )}
                <Field label="Adjuster Email" field="adjuster_email" form={form} set={set} editing={editing} />
                <Field label="Address" field="insurance_address" form={form} set={set} editing={editing} />
                <Field label="Claim #" field="claim_number" form={form} set={set} editing={editing} />
                <Field label="Insured" field="insured" form={form} set={set} editing={editing} />
              </div>
              <Field label="Date of Loss" field="date_of_loss" type="date" form={form} set={set} editing={editing} />
            </div>
          </div>
        </div>
      )}

      {/* Tab 1: Attorneys */}
      {tab === 1 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-700">Attorneys</h3>
            <button onClick={saveAttorneys} disabled={saving} className="px-3 py-1.5 text-sm text-white rounded" style={{ backgroundColor: '#1a2744' }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Client Column */}
            <div className="border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setClientExpanded(v => !v)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-700">Client Attorney</span>
                  <select
                    value={clientAttyType}
                    onChange={e => { e.stopPropagation(); setClientAttyType(e.target.value); }}
                    onClick={e => e.stopPropagation()}
                    className="text-xs border border-gray-300 rounded px-2 py-0.5 bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <option value="Plaintiff">Plaintiff</option>
                    <option value="Defendant">Defendant</option>
                  </select>
                </div>
                <svg className={`w-5 h-5 text-gray-500 transition-transform ${clientExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {clientExpanded && (
                <div className="space-y-3 p-4">
                  <AttorneySelect label="Name" value={clientAtty.name} onChange={handleAttySelect(setClientAtty)} />
                  <Field label="Firm" field="firm" form={clientAtty} set={setClientField} editing={true} />
                  <Field label="Bar #" field="bar_num" form={clientAtty} set={setClientField} editing={true} />
                  <Field label="Address Line 1" field="addr1" form={clientAtty} set={setClientField} editing={true} />
                  <Field label="Address Line 2" field="addr2" form={clientAtty} set={setClientField} editing={true} />
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="City" field="city" form={clientAtty} set={setClientField} editing={true} />
                    <Field label="State" field="state_code" form={clientAtty} set={setClientField} editing={true} />
                    <Field label="Zip" field="zip" form={clientAtty} set={setClientField} editing={true} />
                  </div>
                  <Field label="Phone" field="phone" form={clientAtty} set={setClientField} editing={true} />
                  <Field label="Fax" field="fax" form={clientAtty} set={setClientField} editing={true} />
                  <Field label="Email" field="email" form={clientAtty} set={setClientField} editing={true} />
                </div>
              )}
            </div>

            {/* Opposing Counsel Column */}
            <div className="border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setOpposingExpanded(v => !v)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-700">Opposing Counsel</span>
                  <select
                    value={opposingAttyType}
                    onChange={e => { e.stopPropagation(); setOpposingAttyType(e.target.value); }}
                    onClick={e => e.stopPropagation()}
                    className="text-xs border border-gray-300 rounded px-2 py-0.5 bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <option value="Plaintiff">Plaintiff</option>
                    <option value="Defendant">Defendant</option>
                  </select>
                </div>
                <svg className={`w-5 h-5 text-gray-500 transition-transform ${opposingExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {opposingExpanded && (
                <div className="space-y-3 p-4">
                  <AttorneySelect label="Name" value={opposingAtty.name} onChange={handleAttySelect(setOpposingAtty)} />
                  <Field label="Firm" field="firm" form={opposingAtty} set={setOpposingField} editing={true} />
                  <Field label="Bar #" field="bar_num" form={opposingAtty} set={setOpposingField} editing={true} />
                  <Field label="Address Line 1" field="addr1" form={opposingAtty} set={setOpposingField} editing={true} />
                  <Field label="Address Line 2" field="addr2" form={opposingAtty} set={setOpposingField} editing={true} />
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="City" field="city" form={opposingAtty} set={setOpposingField} editing={true} />
                    <Field label="State" field="state_code" form={opposingAtty} set={setOpposingField} editing={true} />
                    <Field label="Zip" field="zip" form={opposingAtty} set={setOpposingField} editing={true} />
                  </div>
                  <Field label="Phone" field="phone" form={opposingAtty} set={setOpposingField} editing={true} />
                  <Field label="Fax" field="fax" form={opposingAtty} set={setOpposingField} editing={true} />
                  <Field label="Email" field="email" form={opposingAtty} set={setOpposingField} editing={true} />
                </div>
              )}
            </div>
          </div>

          {/* + Add Attorney */}
          <div className="flex justify-center my-6">
            <button
              onClick={() => { setShowAddAttyForm(!showAddAttyForm); setAddAttyForm({ ...EMPTY_ATTY }); }}
              className="px-4 py-2 text-sm text-white rounded"
              style={{ backgroundColor: '#1a2744' }}
            >
              + Add Attorney
            </button>
          </div>

          {showAddAttyForm && (
            <div className="mb-6 p-4 border rounded-lg bg-gray-50">
              <h4 className="font-medium text-gray-700 mb-3">New Additional Attorney</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <AttorneySelect
                  label="Name"
                  value={addAttyForm.name}
                  onChange={(_id, name, _firm, _phone, _email, fullObj) => {
                    if (fullObj) {
                      setAddAttyForm({
                        name, firm: fullObj.firm_name || '', bar_num: fullObj.state_bar_number || '',
                        addr1: fullObj.address || '', addr2: fullObj.address2 || '', city: fullObj.city || '',
                        state_code: fullObj.state_code || '', zip: fullObj.zip_code || '',
                        phone: fullObj.phone || '', fax: fullObj.fax || '', email: fullObj.email || '',
                      });
                    } else {
                      setAddAttyForm(prev => ({ ...prev, name }));
                    }
                  }}
                />
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Firm</label>
                  <input value={addAttyForm.firm} onChange={(e) => setAddAttyForm(prev => ({ ...prev, firm: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Bar #</label>
                  <input value={addAttyForm.bar_num} onChange={(e) => setAddAttyForm(prev => ({ ...prev, bar_num: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                  <input value={addAttyForm.addr1} onChange={(e) => setAddAttyForm(prev => ({ ...prev, addr1: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
                  <input value={addAttyForm.city} onChange={(e) => setAddAttyForm(prev => ({ ...prev, city: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                  <input value={addAttyForm.phone} onChange={(e) => setAddAttyForm(prev => ({ ...prev, phone: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Fax</label>
                  <input value={addAttyForm.fax} onChange={(e) => setAddAttyForm(prev => ({ ...prev, fax: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                  <input value={addAttyForm.email} onChange={(e) => setAddAttyForm(prev => ({ ...prev, email: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => {
                    setAdditionalAttys(prev => [...prev, { ...addAttyForm }]);
                    setShowAddAttyForm(false);
                    setAddAttyForm({ ...EMPTY_ATTY });
                  }}
                  className="px-3 py-1.5 text-sm text-white rounded"
                  style={{ backgroundColor: '#1a2744' }}
                >
                  Add
                </button>
                <button onClick={() => setShowAddAttyForm(false)} className="px-3 py-1.5 text-sm bg-gray-200 rounded">Cancel</button>
              </div>
            </div>
          )}

          {/* Additional Attorneys Table */}
          <div>
            <h4 className="font-semibold text-gray-600 mb-2">Additional Attorneys</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-2 font-medium text-gray-600">Firm</th>
                    <th className="text-left p-2 font-medium text-gray-600">Name</th>
                    <th className="text-left p-2 font-medium text-gray-600">Address</th>
                    <th className="text-left p-2 font-medium text-gray-600">City</th>
                    <th className="text-left p-2 font-medium text-gray-600">Phone</th>
                    <th className="text-left p-2 font-medium text-gray-600">Fax</th>
                    <th className="text-left p-2 font-medium text-gray-600">Bar #</th>
                    <th className="text-left p-2 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {additionalAttys.map((a, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="p-2">{a.firm}</td>
                      <td className="p-2">{a.name}</td>
                      <td className="p-2">{a.addr1}</td>
                      <td className="p-2">{a.city}</td>
                      <td className="p-2">{a.state_code}</td>
                      <td className="p-2">{a.zip}</td>
                      <td className="p-2">{a.phone}</td>
                      <td className="p-2">{a.fax}</td>
                      <td className="p-2">{a.bar_num}</td>
                      <td className="p-2">
                        <button
                          onClick={() => setAdditionalAttys(prev => prev.filter((_, idx) => idx !== i))}
                          className="text-red-600 hover:underline text-xs"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {additionalAttys.length === 0 && (
                    <tr><td colSpan={8} className="p-4 text-center text-gray-400">No additional attorneys</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Stop Location */}
      {tab === 2 && (
        <div className="bg-white rounded-lg shadow p-6">
          {/* Work Orders chip panel */}
          {(cause.work_orders || []).length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Work Orders</span>
                <span className="text-xs text-gray-400">({(cause.work_orders || []).length})</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {(cause.work_orders || []).map(wo => {
                  const isSelected = selectedWO?.id === wo.id;
                  const isEmpty = !wo.stop_location_name;
                  const statusColor = isEmpty
                    ? 'bg-gray-50 text-gray-400 border-gray-200'
                    : ({
                        'Pending': 'bg-blue-100 text-blue-800 border-blue-300',
                        'In Progress': 'bg-yellow-100 text-yellow-800 border-yellow-300',
                        'Completed': 'bg-green-100 text-green-800 border-green-300',
                        'On Hold': 'bg-orange-100 text-orange-800 border-orange-300',
                        'Cancelled': 'bg-red-100 text-red-800 border-red-300',
                      }[wo.status] || 'bg-gray-100 text-gray-700 border-gray-300');
                  return (
                    <button
                      key={wo.id}
                      onClick={() => selectWorkOrder(wo)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        isSelected
                          ? 'ring-2 ring-offset-1 ring-blue-500 ' + statusColor
                          : statusColor + ' hover:ring-2 hover:ring-offset-1 hover:ring-blue-300'
                      }`}
                    >
                      <span className="font-semibold">{wo.wo_number || 'WO'}</span>
                      {wo.stop_location_name ? (
                        <span className="opacity-75">· {wo.stop_location_name.slice(0, 20)}{wo.stop_location_name.length > 20 ? '…' : ''}</span>
                      ) : (
                        <span className="opacity-50">empty</span>
                      )}
                      {wo.rush_order && <span className="text-red-600 font-bold">⚡</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {/* WO selector row */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <h3 className="font-semibold text-gray-700">Stop Location</h3>
              <select
                value={selectedWO?.id || ''}
                onChange={(e) => {
                  const wo = (cause.work_orders || []).find(w => w.id === e.target.value);
                  if (wo) selectWorkOrder(wo);
                }}
                className="px-2 py-1.5 text-sm border rounded bg-white"
              >
                <option value="">-- Select WO --</option>
                {(cause.work_orders || []).map(wo => (
                  <option key={wo.id} value={wo.id}>{wo.wo_number} - {wo.stop_location_name}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate(`/causes/${id}/work-orders/new`)} className="px-3 py-1.5 text-sm text-white rounded" style={{ backgroundColor: '#1a2744' }}>+ Add WO</button>
              {selectedWO && <button onClick={saveWO} disabled={saving} className="px-3 py-1.5 text-sm text-white rounded bg-green-600 hover:bg-green-700">{saving ? 'Saving...' : 'Save'}</button>}
            </div>
          </div>
          {selectedWO ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left section */}
              <div className="space-y-3">
                <StopLocationSelect
                  label="Stop Location Name"
                  value={woForm.stop_location_name || ''}
                  onChange={(id, name, addr1, city, state, zip, phone, fax, email, contactName, typeOfRecord, notes) => {
                    setWoForm(f => ({
                      ...f,
                      stop_location_name: name,
                      stop_location_address: addr1,
                      stop_location_city_state_zip: [city, state, zip].filter(Boolean).join(', '),
                      stop_location_phone: phone,
                      stop_location_fax: fax,
                      stop_location_email: email,
                      stop_location_contact: contactName,
                      stop_location_type_records: typeOfRecord || f.stop_location_type_records,
                      stop_location_notes: notes,
                    }));
                  }}
                />
                <Field label="Add1 Name" field="stop_location_add1_name" form={woForm} set={setWo} editing={true} />
                <Field label="Attention" field="stop_location_attention" form={woForm} set={setWo} editing={true} />
                <TypeOfRecordSelect
                  label="Type / Records"
                  value={woForm.stop_location_type_records || ''}
                  onChange={v => setWoForm(f => ({...f, stop_location_type_records: v}))}
                />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Record Date From" field="record_date_from" type="date" form={woForm} set={setWo} editing={true} />
                  <Field label="Record Date To" field="record_date_to" type="date" form={woForm} set={setWo} editing={true} />
                </div>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={woForm.stop_location_include_prior_dates || false} onChange={(e) => setWo('stop_location_include_prior_dates', e.target.checked)} />
                    Include Prior Dates
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={woForm.stop_location_include_to_present || false} onChange={(e) => setWo('stop_location_include_to_present', e.target.checked)} />
                    Include to Present
                  </label>
                </div>
                <Field label="Address" field="stop_location_address" form={woForm} set={setWo} editing={true} />
                <Field label="Adrs line 2" field="stop_location_addr2" form={woForm} set={setWo} editing={true} />
                <Field label="City/State/Zip" field="stop_location_city_state_zip" form={woForm} set={setWo} editing={true} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Phone" field="stop_location_phone" form={woForm} set={setWo} editing={true} />
                  <Field label="FAX" field="stop_location_fax" form={woForm} set={setWo} editing={true} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Email" field="stop_location_email" form={woForm} set={setWo} editing={true} />
                  <Field label="Website" field="stop_location_website" form={woForm} set={setWo} editing={true} />
                </div>
                <Field label="Contact" field="stop_location_contact" form={woForm} set={setWo} editing={true} />
                <Field label="Agent of Service" field="stop_location_agent_of_service" form={woForm} set={setWo} editing={true} />
                <Field label="Service Type" field="stop_location_service_type" form={woForm} set={setWo} editing={true} />
                <Field label="Requirements" field="stop_location_requirements" form={woForm} set={setWo} editing={true} />
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-0.5">Stop Location Notes</label>
                  <textarea value={woForm.stop_location_notes || ''} onChange={(e) => setWo('stop_location_notes', e.target.value)} rows={3} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-yellow-50 focus:ring-1 focus:ring-blue-500 focus:outline-none" />
                </div>
              </div>
              {/* Right section */}
              <div className="space-y-3">
                <div>
                  
                  <QuestionSelect
                    label="Question Text"
                    value={woForm.question_text || ''}
                    onChange={v => setWoForm(f => ({...f, question_text: v}))}
                  />
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-400">Select a work order above or create a new one</p>
          )}
        </div>
      )}

      {/* Tab 3: Work Order Update */}
      {tab === 3 && (
        <div className="bg-white rounded-lg shadow p-6">
          {selectedWO ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">Update Work Order: {selectedWO.wo_number}</h3>
                <button onClick={saveWO} disabled={saving} className="px-3 py-1.5 text-sm text-white rounded" style={{ backgroundColor: '#1a2744' }}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>

              {/* Top header fields */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                <Field label="Legal Tech" field="legal_tech" form={woForm} set={setWo} editing={true} />
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">Status</label>
                  <select value={woForm.status || ''} onChange={(e) => setWo('status', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <Field label="Status Date" field="status_date" type="date" form={woForm} set={setWo} editing={true} />
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">Notes Entry</label>
                  <div className="flex gap-1">
                    <input value={woForm.notes_entry || ''} onChange={(e) => setWo('notes_entry', e.target.value)} className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded" />
                    <button onClick={saveWO} className="px-2 py-1.5 text-xs text-white rounded bg-blue-600 hover:bg-blue-700">Save</button>
                  </div>
                </div>
              </div>

              {/* Status tracking dates */}
              <h4 className="font-semibold text-gray-600 border-b pb-1 mb-3">Status Tracking</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                <Field label="Next Call" field="next_call_date" type="date" form={woForm} set={setWo} editing={true} />
                <Field label="Served" field="served_date" type="date" form={woForm} set={setWo} editing={true} />
                <Field label="Set" field="set_date" type="date" form={woForm} set={setWo} editing={true} />
                <Field label="Waiver" field="waiver_date" type="date" form={woForm} set={setWo} editing={true} />
                <Field label="Closed" field="closed_date" type="date" form={woForm} set={setWo} editing={true} />
                <Field label="Cancel" field="cancel_date" type="date" form={woForm} set={setWo} editing={true} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-0.5">Add1 Notes</label>
                  <textarea value={woForm.add1_notes || ''} onChange={(e) => setWo('add1_notes', e.target.value)} rows={2} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-0.5">General Notes</label>
                  <textarea value={woForm.general_notes || ''} onChange={(e) => setWo('general_notes', e.target.value)} rows={2} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-yellow-50" />
                </div>
              </div>

              {/* Payment section */}
              <h4 className="font-semibold text-gray-600 border-b pb-1 mb-3">Payment</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
                <Field label="Ck# (99=CC)" field="check_number" form={woForm} set={setWo} editing={true} />
                <Field label="Amt" field="payment_amount" type="number" form={woForm} set={setWo} editing={true} />
                <Field label="Payment Date" field="payment_date" type="date" form={woForm} set={setWo} editing={true} />
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={woForm.prepay || false} onChange={(e) => setWo('prepay', e.target.checked)} />
                    PP Prepay
                  </label>
                </div>
                <Field label="Paid to" field="paid_to" form={woForm} set={setWo} editing={true} />
              </div>
              <button
                onClick={() => {
                  const p = { check_number: woForm.check_number || '', amount: woForm.payment_amount || 0, date: woForm.payment_date || '', paid_to: woForm.paid_to || '' };
                  setWo('payments', [...(woForm.payments || []), p]);
                }}
                className="px-3 py-1 text-xs text-white rounded bg-blue-600 hover:bg-blue-700 mb-3"
              >
                + Add Payment
              </button>
              {(woForm.payments || []).length > 0 && (
                <table className="w-full text-sm mb-4">
                  <thead><tr className="border-b bg-gray-50"><th className="text-left p-1.5 text-xs text-gray-600">Ck#</th><th className="text-right p-1.5 text-xs text-gray-600">Amount</th><th className="text-left p-1.5 text-xs text-gray-600">Date</th><th className="text-left p-1.5 text-xs text-gray-600">Paid To</th><th className="p-1.5"></th></tr></thead>
                  <tbody>
                    {(woForm.payments || []).map((p, i) => (
                      <tr key={i} className="border-b"><td className="p-1.5">{p.check_number}</td><td className="p-1.5 text-right">${Number(p.amount || 0).toFixed(2)}</td><td className="p-1.5">{p.date}</td><td className="p-1.5">{p.paid_to}</td><td className="p-1.5"><button onClick={() => setWo('payments', (woForm.payments || []).filter((_, idx) => idx !== i))} className="text-red-600 text-xs hover:underline">Remove</button></td></tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Read-only stop location display */}
              <h4 className="font-semibold text-gray-600 border-b pb-1 mb-3">Stop Location (Read-Only)</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 rounded bg-blue-50">
                <div><label className="block text-xs text-gray-500">Name</label><div className="px-2 py-1 text-sm bg-gray-100 border rounded">{woForm.stop_location_name || '-'}</div></div>
                <div><label className="block text-xs text-gray-500">Address</label><div className="px-2 py-1 text-sm bg-gray-100 border rounded">{woForm.stop_location_address || '-'}</div></div>
                <div><label className="block text-xs text-gray-500">City/State/Zip</label><div className="px-2 py-1 text-sm bg-gray-100 border rounded">{woForm.stop_location_city_state_zip || '-'}</div></div>
                <div><label className="block text-xs text-gray-500">Phone</label><div className="px-2 py-1 text-sm bg-gray-100 border rounded">{woForm.stop_location_phone || '-'}</div></div>
                <div><label className="block text-xs text-gray-500">FAX</label><div className="px-2 py-1 text-sm bg-gray-100 border rounded">{woForm.stop_location_fax || '-'}</div></div>
                <div><label className="block text-xs text-gray-500">Contact</label><div className="px-2 py-1 text-sm bg-gray-100 border rounded">{woForm.stop_location_contact || '-'}</div></div>
              </div>
            </>
          ) : (
            <p className="text-gray-400">Select a work order from the Stop Location tab</p>
          )}
        </div>
      )}

      {/* Tab 4: Work Order Pg 2 */}
      {tab === 4 && (
        <div className="bg-white rounded-lg shadow p-6">
          {selectedWO ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">Work Order Pg 2: {selectedWO.wo_number}</h3>
                <button onClick={saveWO} disabled={saving} className="px-3 py-1.5 text-sm text-white rounded" style={{ backgroundColor: '#1a2744' }}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left column */}
                <div className="space-y-4">
                  {/* Ordered Records */}
                  <h4 className="font-semibold text-gray-600 border-b pb-1">Ordered Records</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Firm 2nd Atty" field="firm_2nd_attorney" form={woForm} set={setWo} editing={true} />
                    <Field label="Name" field="name_2nd_attorney" form={woForm} set={setWo} editing={true} />
                  </div>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={woForm.records_requested_type || false} onChange={(e) => setWo('records_requested_type', e.target.checked)} /> Records</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={woForm.radiology_requested || false} onChange={(e) => setWo('radiology_requested', e.target.checked)} /> Radiology</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={woForm.rule_11_requested || false} onChange={(e) => setWo('rule_11_requested', e.target.checked)} /> Rule 11</label>
                  </div>
                  <SelectField label="Review" field="review_type" options={['Review', 'Partial Review', 'Full Review', 'No Review']} form={woForm} set={setWo} editing={true} />

                  {/* Others table */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-xs font-medium text-gray-600">Others</label>
                      <button onClick={() => setWo('others_table', [...(woForm.others_table || []), { firm: '', name: '', records: false, radiology: false, rule_11: false, review: '' }])} className="text-xs text-blue-600 hover:underline">+ Add</button>
                    </div>
                    {(woForm.others_table || []).length > 0 && (
                      <table className="w-full text-xs border">
                        <thead><tr className="bg-gray-50"><th className="p-1 text-left">Firm</th><th className="p-1 text-left">Name</th><th className="p-1">Rec</th><th className="p-1">Rad</th><th className="p-1">R11</th><th className="p-1"></th></tr></thead>
                        <tbody>
                          {(woForm.others_table || []).map((o, i) => (
                            <tr key={i} className="border-t">
                              <td className="p-1"><input value={o.firm || ''} onChange={(e) => { const arr = [...(woForm.others_table || [])]; arr[i] = { ...arr[i], firm: e.target.value }; setWo('others_table', arr); }} className="w-full px-1 py-0.5 text-xs border rounded" /></td>
                              <td className="p-1"><input value={o.name || ''} onChange={(e) => { const arr = [...(woForm.others_table || [])]; arr[i] = { ...arr[i], name: e.target.value }; setWo('others_table', arr); }} className="w-full px-1 py-0.5 text-xs border rounded" /></td>
                              <td className="p-1 text-center"><input type="checkbox" checked={o.records || false} onChange={(e) => { const arr = [...(woForm.others_table || [])]; arr[i] = { ...arr[i], records: e.target.checked }; setWo('others_table', arr); }} /></td>
                              <td className="p-1 text-center"><input type="checkbox" checked={o.radiology || false} onChange={(e) => { const arr = [...(woForm.others_table || [])]; arr[i] = { ...arr[i], radiology: e.target.checked }; setWo('others_table', arr); }} /></td>
                              <td className="p-1 text-center"><input type="checkbox" checked={o.rule_11 || false} onChange={(e) => { const arr = [...(woForm.others_table || [])]; arr[i] = { ...arr[i], rule_11: e.target.checked }; setWo('others_table', arr); }} /></td>
                              <td className="p-1"><button onClick={() => setWo('others_table', (woForm.others_table || []).filter((_, idx) => idx !== i))} className="text-red-600 hover:underline">x</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Tracking section */}
                  <h4 className="font-semibold text-gray-600 border-b pb-1 pt-2">Tracking</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Number" field="tracking_number" form={woForm} set={setWo} editing={true} />
                    <Field label="Courier" field="courier" form={woForm} set={setWo} editing={true} />
                  </div>
                  <Field label="Courier Note" field="courier_note" form={woForm} set={setWo} editing={true} />
                  <div className="flex gap-4 flex-wrap">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={woForm.notary || false} onChange={(e) => setWo('notary', e.target.checked)} /> Notary</label>
                    {woForm.notary && <Field label="Phone" field="notary_phone" form={woForm} set={setWo} editing={true} />}
                  </div>
                  <div className="flex gap-4 flex-wrap">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={woForm.certified || false} onChange={(e) => setWo('certified', e.target.checked)} /> Certified</label>
                    {woForm.certified && <Field label="Details" field="certified_text" form={woForm} set={setWo} editing={true} />}
                  </div>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={woForm.hand_served || false} onChange={(e) => setWo('hand_served', e.target.checked)} /> Hand Served</label>
                  <label className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded ${woForm.rush_order ? 'bg-red-100 text-red-800' : 'text-gray-700'}`}>
                    <input type="checkbox" checked={woForm.rush_order || false} onChange={(e) => setWo('rush_order', e.target.checked)} /> Rush Order
                  </label>
                </div>

                {/* Right column */}
                <div className="space-y-4">
                  {/* Checkboxes */}
                  <h4 className="font-semibold text-gray-600 border-b pb-1">Options</h4>
                  <div className="flex gap-4 flex-wrap">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={woForm.cross_questions || false} onChange={(e) => setWo('cross_questions', e.target.checked)} /> Cross Questions</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={woForm.amended || false} onChange={(e) => setWo('amended', e.target.checked)} /> Amended</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={woForm.redirects || false} onChange={(e) => setWo('redirects', e.target.checked)} /> Redirects</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={woForm.copies || false} onChange={(e) => setWo('copies', e.target.checked)} /> Copies</label>
                  </div>

                  {/* Completed information */}
                  <h4 className="font-semibold text-gray-600 border-b pb-1 pt-2">Completed</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Pages" field="pages_count" type="number" form={woForm} set={setWo} editing={true} />
                    <Field label="Films" field="films_count" type="number" form={woForm} set={setWo} editing={true} />
                    <Field label="Color Pages" field="color_pages_count" type="number" form={woForm} set={setWo} editing={true} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="CDs Old Vers" field="cds_count" type="number" form={woForm} set={setWo} editing={true} />
                    <Field label="Other" field="other_description" form={woForm} set={setWo} editing={true} />
                  </div>

                  {/* CD Items */}
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="QTY" field="cd_qty" type="number" form={woForm} set={setWo} editing={true} />
                    <SelectField label="Description" field="cd_type" options={['CD', 'DVD', 'USB', 'Digital Download']} form={woForm} set={setWo} editing={true} />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-xs font-medium text-gray-600">CD Items</label>
                      <button onClick={() => setWo('cd_items', [...(woForm.cd_items || []), { qty: 0, description: '' }])} className="text-xs text-blue-600 hover:underline">+ Add</button>
                    </div>
                    {(woForm.cd_items || []).length > 0 && (
                      <table className="w-full text-xs border">
                        <thead><tr className="bg-gray-50"><th className="p-1 text-left">QTY</th><th className="p-1 text-left">Description</th><th className="p-1"></th></tr></thead>
                        <tbody>
                          {(woForm.cd_items || []).map((item, i) => (
                            <tr key={i} className="border-t">
                              <td className="p-1"><input type="number" value={item.qty || 0} onChange={(e) => { const arr = [...(woForm.cd_items || [])]; arr[i] = { ...arr[i], qty: parseInt(e.target.value) || 0 }; setWo('cd_items', arr); }} className="w-full px-1 py-0.5 text-xs border rounded" /></td>
                              <td className="p-1"><input value={item.description || ''} onChange={(e) => { const arr = [...(woForm.cd_items || [])]; arr[i] = { ...arr[i], description: e.target.value }; setWo('cd_items', arr); }} className="w-full px-1 py-0.5 text-xs border rounded" /></td>
                              <td className="p-1"><button onClick={() => setWo('cd_items', (woForm.cd_items || []).filter((_, idx) => idx !== i))} className="text-red-600 hover:underline">x</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Production verification */}
                  <h4 className="font-semibold text-gray-600 border-b pb-1 pt-2">Production Verification</h4>
                  <Field label="Front Check Performed By" field="front_check_by" form={woForm} set={setWo} editing={true} />
                  <Field label="Back Check Performed By" field="back_check_by" form={woForm} set={setWo} editing={true} />
                  <Field label="Double Check Performed By" field="double_check_by" form={woForm} set={setWo} editing={true} />
                  <Field label="Box Number" field="box_number" form={woForm} set={setWo} editing={true} />
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={woForm.shredded || false} onChange={(e) => setWo('shredded', e.target.checked)} /> Shredded</label>
                </div>
              </div>
            </>
          ) : (
            <p className="text-gray-400">Select a work order from the Stop Location tab</p>
          )}
        </div>
      )}

      {/* Tab 5: Invoice */}
      {tab === 5 && (() => { if (allCauseInvoices.length === 0) loadAllCauseInvoices(); return null; })()}
      {tab === 5 && (
        <div className="bg-white rounded-lg shadow p-6">
          {/* Invoice tab header with Generate Invoice button */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-700">Invoices</h3>
            <button
              onClick={() => setShowInvoiceMerge(true)}
              className="px-4 py-2 text-white rounded text-sm font-medium flex items-center gap-2"
              style={{ backgroundColor: '#1a2744' }}
            >
              💳 Generate Invoice
            </button>
          </div>

          {/* All cause invoices summary */}
          {allCauseInvoices.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-blue-800">All Invoices — This Case</h3>
                <span className="text-sm font-bold text-blue-700">
                  Total: ${allCauseInvoices.reduce((s, i) => s + (i.amount || 0), 0).toFixed(2)}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-blue-200">
                    <th className="text-left p-1.5 font-medium text-blue-700">WO Invoice #</th>
                    <th className="text-left p-1.5 font-medium text-blue-700">Global #</th>
                    <th className="text-left p-1.5 font-medium text-blue-700">WO #</th>
                    <th className="text-left p-1.5 font-medium text-blue-700">Template</th>
                    <th className="text-right p-1.5 font-medium text-blue-700">Amount</th>
                    <th className="text-left p-1.5 font-medium text-blue-700">Status</th>
                    <th className="text-left p-1.5 font-medium text-blue-700">Date</th>
                  </tr></thead>
                  <tbody>
                    {allCauseInvoices.map(inv => (
                      <tr key={inv.id} className="border-b border-blue-100 hover:bg-blue-100 cursor-pointer" onClick={() => setSelectedInvoice(inv)}>
                        <td className="p-1.5 font-mono font-bold text-blue-900 underline decoration-dotted">{inv.wo_invoice_number || inv.invoice_number}</td>
                        <td className="p-1.5 font-mono text-gray-500">{inv.invoice_number}</td>
                        <td className="p-1.5">{inv.wo_number || '-'}</td>
                        <td className="p-1.5 text-gray-500 truncate max-w-[120px]">{inv.template_name || inv.description || '-'}</td>
                        <td className="p-1.5 text-right font-medium">${(inv.amount||0).toFixed(2)}</td>
                        <td className="p-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${inv.status==='Paid'?'bg-green-100 text-green-800':inv.status==='Sent'?'bg-blue-100 text-blue-800':'bg-gray-100 text-gray-600'}`}>{inv.status}</span>
                        </td>
                        <td className="p-1.5 text-gray-500">{(inv.invoice_date || inv.created_at || '').split('T')[0]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {selectedWO ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">Invoices for: {selectedWO.wo_number}</h3>
                <button
                  onClick={() => { setShowInvForm(true); setInvForm({ amount: '', description: '', status: 'Draft', invoice_date: '', due_date: '', notes: '' }); }}
                  className="px-3 py-1.5 text-sm text-white rounded" style={{ backgroundColor: '#1a2744' }}
                >
                  + Add Invoice
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-2 font-medium text-gray-600">Invoice #</th>
                      <th className="text-left p-2 font-medium text-gray-600">WO #</th>
                      <th className="text-left p-2 font-medium text-gray-600">Description</th>
                      <th className="text-right p-2 font-medium text-gray-600">Amount</th>
                      <th className="text-left p-2 font-medium text-gray-600">Status</th>
                      <th className="text-left p-2 font-medium text-gray-600">Invoice Date</th>
                      <th className="text-left p-2 font-medium text-gray-600">Due Date</th>
                      <th className="text-left p-2 font-medium text-gray-600">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{inv.invoice_number}</td>
                        <td className="p-2 text-gray-500">{selectedWO.wo_number}</td>
                        <td className="p-2">{inv.description}</td>
                        <td className="p-2 text-right">${(inv.amount || 0).toFixed(2)}</td>
                        <td className="p-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            inv.status === 'Paid' ? 'bg-green-100 text-green-800' :
                            inv.status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>{inv.status}</span>
                        </td>
                        <td className="p-2 text-gray-500">{inv.invoice_date || inv.created_at?.split('T')[0]}</td>
                        <td className="p-2 text-gray-500">{inv.due_date || '-'}</td>
                        <td className="p-2 text-gray-500 max-w-[150px] truncate">{inv.notes || '-'}</td>
                      </tr>
                    ))}
                    {invoices.length === 0 && (
                      <tr><td colSpan={8} className="p-4 text-center text-gray-400">No invoices</td></tr>
                    )}
                  </tbody>
                  {invoices.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 font-semibold">
                        <td className="p-2" colSpan={3}>Total</td>
                        <td className="p-2 text-right">${invoices.reduce((s, i) => s + (i.amount || 0), 0).toFixed(2)}</td>
                        <td colSpan={4}></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {showInvForm && (
                <div className="mt-4 p-4 border rounded-lg bg-gray-50">
                  <h4 className="font-medium text-gray-700 mb-3">New Invoice</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Amount ($)</label>
                      <input type="number" step="0.01" value={invForm.amount} onChange={(e) => setInvForm((f) => ({ ...f, amount: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                      <input value={invForm.description} onChange={(e) => setInvForm((f) => ({ ...f, description: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                      <select value={invForm.status} onChange={(e) => setInvForm((f) => ({ ...f, status: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded bg-white">
                        <option value="Draft">Draft</option>
                        <option value="Sent">Sent</option>
                        <option value="Paid">Paid</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Invoice Date</label>
                      <input type="date" value={invForm.invoice_date || ''} onChange={(e) => setInvForm((f) => ({ ...f, invoice_date: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Due Date</label>
                      <input type="date" value={invForm.due_date || ''} onChange={(e) => setInvForm((f) => ({ ...f, due_date: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                      <input value={invForm.notes || ''} onChange={(e) => setInvForm((f) => ({ ...f, notes: e.target.value }))} className="w-full px-2 py-1.5 text-sm border rounded" />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={saveInvoice} disabled={saving} className="px-3 py-1.5 text-sm text-white rounded" style={{ backgroundColor: '#1a2744' }}>
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => setShowInvForm(false)} className="px-3 py-1.5 text-sm bg-gray-200 rounded">Cancel</button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-400">Select a work order from the Stop Location tab</p>
          )}
        </div>
      )}
      {showMerge && <MergeDocument causeId={id} onClose={() => setShowMerge(false)} invoiceMode={false} />}
      {showInvoiceMerge && <MergeDocument causeId={id} onClose={() => { setShowInvoiceMerge(false); loadAllCauseInvoices(); }} invoiceMode={true} />}

      {/* Copy Case Modal */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ backgroundColor: '#2d6a4f' }}>
              <div>
                <h2 className="text-white font-bold text-lg">📋 Copy Case</h2>
                <p className="text-green-200 text-xs">Creates a new case + all work orders. Edit fields below before saving.</p>
              </div>
              <button onClick={() => setShowCopyModal(false)} className="text-white hover:text-gray-200 text-2xl">&times;</button>
            </div>
            <div className="px-6 py-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: 'New Cause Number *', key: 'cause_number' },
                  { label: 'File Number', key: 'file_number' },
                  { label: 'Patient Name', key: 'patient_name' },
                  { label: 'Patient Last Name', key: 'patient_last_name' },
                  { label: 'Date of Birth', key: 'patient_dob', type: 'date' },
                  { label: 'Order Date', key: 'order_date', type: 'date' },
                  { label: 'Plaintiff', key: 'style_plaintiff' },
                  { label: 'Defendant', key: 'style_defendant' },
                  { label: 'County', key: 'county' },
                  { label: 'Court Number', key: 'court_number' },
                  { label: 'Type of Case', key: 'type_of_case' },
                ].map(({ label, key, type }) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                    <input
                      type={type || 'text'}
                      value={copyForm[key] || ''}
                      onChange={e => setCopyForm(f => ({ ...f, [key]: e.target.value }))}
                      className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                    />
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                ⚠️ All work orders from this case will be copied with new WO numbers. Status resets to <strong>Order Intake</strong>. Attorneys are copied as-is.
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex gap-3 rounded-b-xl">
              <button
                onClick={async () => {
                  if (!copyForm.cause_number?.trim()) { alert('Cause number is required'); return; }
                  setCopying(true);
                  try {
                    const res = await axios.post(
                      `${API_URL}/api/causes/${id}/copy`,
                      copyForm,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    const newCause = res.data;
                    setShowCopyModal(false);
                    // Navigate to new case in edit mode
                    navigate(`/causes/${newCause.id}`, { state: { editing: true } });
                  } catch (err) {
                    alert('Copy failed: ' + (err.response?.data?.detail || err.message));
                  } finally {
                    setCopying(false);
                  }
                }}
                disabled={copying}
                className="flex-1 py-2 text-white rounded font-medium disabled:opacity-50"
                style={{ backgroundColor: '#2d6a4f' }}
              >
                {copying ? 'Copying...' : '📋 Create Copy'}
              </button>
              <button onClick={() => setShowCopyModal(false)} className="px-4 py-2 bg-gray-200 rounded text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ backgroundColor: '#1a2744' }}>
              <div>
                <div className="text-white font-bold text-lg">{selectedInvoice.wo_invoice_number || selectedInvoice.invoice_number}</div>
                <div className="text-blue-200 text-xs">{selectedInvoice.invoice_number} &middot; {selectedInvoice.wo_number}</div>
              </div>
              <button onClick={() => setSelectedInvoice(null)} className="text-white hover:text-gray-300 text-2xl leading-none">&times;</button>
            </div>

            {/* Body */}
            <div className="px-6 py-4">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <div className="text-xs text-gray-500">Template</div>
                  <div className="text-sm font-medium">{selectedInvoice.template_name || '-'}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">Date</div>
                  <div className="text-sm">{(selectedInvoice.invoice_date || selectedInvoice.created_at || '').split('T')[0]}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">Status</div>
                  <select
                    value={selectedInvoice.status || 'Draft'}
                    onChange={async e => {
                      const newStatus = e.target.value;
                      await axios.put(`${API_URL}/api/invoices/${selectedInvoice.id}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
                      setSelectedInvoice(s => ({...s, status: newStatus}));
                      loadAllCauseInvoices();
                    }}
                    className="text-sm border rounded px-2 py-0.5 bg-white"
                  >
                    <option value="Draft">Draft</option>
                    <option value="Sent">Sent</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>
              </div>

              {/* Fee line items */}
              {selectedInvoice.base_fee !== undefined ? (
                <table className="w-full text-sm mb-4">
                  <tbody className="divide-y divide-gray-100">
                    {[
                      ['Base Fee', selectedInvoice.base_fee],
                      ['Custodian Pull Fee', selectedInvoice.custodian_pull_fee],
                      [`Pages/Exhibits (${selectedInvoice.pages_count || 0} pages × $0.50)`, selectedInvoice.pages_exhibits],
                      [`Printed Copy Pages (${selectedInvoice.pages_count || 0} pages × $0.25)`, selectedInvoice.printed_copy_pages],
                      ['Notice Certification', selectedInvoice.notice_certification],
                      [`Trip Charge${selectedInvoice.hand_served ? ' (Hand Served)' : ''}`, selectedInvoice.trip_charge],
                      ['Custodian Contact Fee', selectedInvoice.custodian_contact_fee],
                      ['Court Filing Certificate', selectedInvoice.court_filing_certificate],
                      ['Electronic Delivery', selectedInvoice.electronic_delivery],
                    ].map(([label, val]) => (
                      <tr key={label}>
                        <td className="py-1.5 text-gray-600">{label}</td>
                        <td className="py-1.5 text-right font-medium">${(val || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-300">
                      <td className="py-2 font-bold text-gray-800">Total</td>
                      <td className="py-2 text-right font-bold text-lg" style={{ color: '#1a2744' }}>${(selectedInvoice.amount || 0).toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <div className="text-center py-4">
                  <div className="text-2xl font-bold" style={{ color: '#1a2744' }}>${(selectedInvoice.amount || 0).toFixed(2)}</div>
                  <div className="text-gray-500 text-sm mt-1">{selectedInvoice.description}</div>
                </div>
              )}

              {selectedInvoice.notes && (
                <div className="text-xs text-gray-500 border-t pt-2">Notes: {selectedInvoice.notes}</div>
              )}
            </div>

            <div className="px-6 py-3 border-t bg-gray-50 rounded-b-xl flex items-center justify-between">
              <div>
                {selectedInvoice.docx_content_b64 && (
                  <a
                    href={`${API_URL}/api/invoices/${selectedInvoice.id}/download`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={e => {
                      e.preventDefault();
                      fetch(`${API_URL}/api/invoices/${selectedInvoice.id}/download`, {
                        headers: { Authorization: `Bearer ${token}` }
                      }).then(r => r.blob()).then(blob => {
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${selectedInvoice.wo_invoice_number || selectedInvoice.invoice_number}.docx`;
                        a.click();
                        URL.revokeObjectURL(url);
                      });
                    }}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-white rounded"
                    style={{ backgroundColor: '#1a2744' }}
                  >
                    📄 Download Invoice Doc
                  </a>
                )}
              </div>
              <button onClick={() => setSelectedInvoice(null)} className="px-4 py-2 bg-gray-200 rounded text-sm">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
